import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown, Trash2, Pencil, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { inr, dmy } from "@/lib/format";
import { PageHeader, Panel, StatusBadge, EmptyState, ConfirmDelete, MobileCards, MobileCard } from "@/components/common";

export default function Quotations() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/quotations").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const pdf = (q) => downloadFile(`/quotations/${q.id}/pdf`, `${q.quote_no.replace(/\//g, "-")}.pdf`).catch(() => toast.error("PDF failed"));
  const remove = async (id) => { await api.delete(`/quotations/${id}`); toast.success("Quotation deleted"); load(); };

  return (
    <div data-testid="quotations-page">
      <PageHeader eyebrow="Documents" title="Quotations" subtitle="Professional quotes for corporate and retail trips."
        actions={<Button asChild className="h-11 sm:h-10" data-testid="new-quotation-btn"><Link to="/quotations/new"><Plus className="h-4 w-4 mr-1.5" /> New Quotation</Link></Button>} />
      <Panel>
        {!loading && rows.length === 0 ? (
          <EmptyState icon={FileSignature} title="No quotations yet" action={<Button asChild><Link to="/quotations/new">Create quotation</Link></Button>} />
        ) : (<>
          <MobileCards items={rows} testid="quotations-mobile-list" render={(q) => (
            <MobileCard key={q.id} testid={`quotation-card-${q.id}`} onClick={() => nav(`/quotations/${q.id}`)} title={q.client_name}
              subtitle={<span><span className="font-mono">{q.quote_no}</span> · {dmy(q.quote_date)}{q.client_company ? ` · ${q.client_company}` : ""}</span>}
              right={<StatusBadge value={q.status} />}
              rows={[["Total", <span className="num font-semibold">{inr(q.grand_total)}</span>], ["Valid until", dmy(q.valid_until)], q.trip_details && ["Trip", q.trip_details]]}
              actions={<>
                <Button size="sm" variant="outline" className="h-10 flex-1" onClick={() => pdf(q)} data-testid={`quotation-card-pdf-${q.id}`}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
                <Button size="sm" variant="outline" className="h-10 flex-1" asChild><Link to={`/quotations/${q.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Link></Button>
                <ConfirmDelete onConfirm={() => remove(q.id)} trigger={<Button size="sm" variant="outline" className="h-10 text-rose-600"><Trash2 className="h-4 w-4" /></Button>} />
              </>} />
          )} />
          <div className="hidden md:block overflow-x-auto -mx-4 sm:-mx-5">
            <table className="w-full text-sm" data-testid="quotations-table">
              <thead><tr className="border-b text-left">{["Quote No", "Date", "Client", "Trip", "Total", "Status", ""].map((h) => <th key={h} className="px-4 py-2.5 eyebrow whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>{rows.map((q) => (
                <tr key={q.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => nav(`/quotations/${q.id}`)} data-testid={`quotation-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">{q.quote_no}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{dmy(q.quote_date)}<div className="text-xs text-muted-foreground">Valid {dmy(q.valid_until)}</div></td>
                  <td className="px-4 py-3"><div className="font-medium">{q.client_name}</div><div className="text-xs text-muted-foreground">{q.client_company}</div></td>
                  <td className="px-4 py-3 text-xs max-w-[240px] truncate">{q.trip_details || "-"}</td>
                  <td className="px-4 py-3 num font-semibold">{inr(q.grand_total)}</td>
                  <td className="px-4 py-3"><StatusBadge value={q.status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => pdf(q)} data-testid={`quotation-pdf-${q.id}`}><FileDown className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" asChild><Link to={`/quotations/${q.id}`} data-testid={`quotation-edit-${q.id}`}><Pencil className="h-4 w-4" /></Link></Button>
                    <ConfirmDelete onConfirm={() => remove(q.id)} trigger={<Button variant="ghost" size="icon" className="text-rose-600" data-testid={`quotation-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>)}
      </Panel>
    </div>
  );
}
